import { Currency } from "@redotech/money/currencies";
import { DEFAULT_CURRENCY, moneyFormat } from "@redotech/money/util";
import { RateTableType } from "@redotech/redo-conversion-checkout-shipping-common/shipping-rate-table-enums";
import { ShippingRateTable } from "@redotech/redo-conversion-core-common/redo-in-shipping/shipping-rate-table";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { safeParseEnum } from "@redotech/util/enum";
import classNames from "classnames";
import { memo, useMemo } from "react";
import * as css from "./shipping-options-checkout-preview.module.css";

/**
 * Renders a checkout experience's shipping methods the way they appear in the
 * Shopify checkout shipping-method selector: a radio list with the cheapest
 * (first) option pre-selected. This is a visual preview, not the configuration
 * card — it shows all possible methods in their configured order.
 *
 * Pricing per rate type:
 *  - FIXED: the configured price.
 *  - DYNAMIC: a representative price within the rate's bounds, kept stable per
 *    rate (seeded off its id) so it doesn't change on every hover.
 *  - CUSTOM: rendered as "Calculated at checkout" (price is merchant-code-driven
 *    and not statically known).
 *
 * Shipping discount rules are applied to FIXED/DYNAMIC rows, showing the
 * discounted price, the struck-through original, and a savings line.
 */
export const ShippingOptionsCheckoutPreview = memo(
  function ShippingOptionsCheckoutPreview({
    rateTables,
  }: {
    rateTables: ShippingRateTable[];
  }) {
    const rows = useMemo(
      () => rateTables.map(rateTableToShippingPreviewRow),
      [rateTables],
    );

    if (rows.length === 0) {
      return (
        <Text fontSize="xs" textColor="secondary">
          No shipping methods configured.
        </Text>
      );
    }

    return (
      <div className={css.container}>
        {rows.map((row, index) => {
          const selected = index === 0;
          return (
            <div
              className={classNames(
                css.row,
                index === 0 && css.rowFirst,
                index === rows.length - 1 && css.rowLast,
                selected && css.rowSelected,
              )}
              key={row.id}
            >
              <Flex align="flex-start" flex={1} gap="md" overflow="hidden">
                <div
                  className={classNames(
                    css.radioDot,
                    selected && css.radioDotSelected,
                  )}
                />
                <Flex dir="column" flex={1} gap="xxs" overflow="hidden">
                  <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                    {row.title}
                  </Text>
                  {row.description && (
                    <Text fontSize="xs" textColor="secondary">
                      {row.description}
                    </Text>
                  )}
                  {row.savingsLabel && (
                    <Text fontSize="xs" fontWeight="medium" textColor="success">
                      {row.savingsLabel}
                    </Text>
                  )}
                </Flex>
              </Flex>
              <Flex align="flex-end" dir="column" gap="xxs" shrink={0}>
                {row.calculatedExplanation ? (
                  <Tooltip
                    darkMode
                    leftAlign
                    placement="top"
                    title={row.calculatedExplanation}
                  >
                    <div className={css.calculatedBadge}>
                      <RedoBadge color="gray" size="sm" text="--" />
                    </div>
                  </Tooltip>
                ) : (
                  <>
                    {row.originalPriceLabel && (
                      <Text
                        className={css.strikethrough}
                        fontSize="xs"
                        textColor="secondary"
                      >
                        {row.originalPriceLabel}
                      </Text>
                    )}
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      textColor="primary"
                    >
                      {row.priceLabel}
                    </Text>
                  </>
                )}
              </Flex>
            </div>
          );
        })}
      </div>
    );
  },
);

interface ShippingPreviewRow {
  id: string;
  title: string;
  description: string;
  /** Set for fixed rates that have a concrete price (or "FREE"). */
  priceLabel?: string;
  originalPriceLabel?: string;
  savingsLabel?: string;
  /**
   * Set for dynamic/custom rates whose price isn't known until checkout. The
   * row shows a "--" badge with this text as a hover tooltip explaining how the
   * price gets calculated.
   */
  calculatedExplanation?: string;
}

function rateTableToShippingPreviewRow(
  rateTable: ShippingRateTable,
): ShippingPreviewRow {
  const base = {
    id: rateTable._id,
    title: rateTable.name,
    description: rateTable.description,
  };

  if (rateTable.type === RateTableType.CUSTOM) {
    return {
      ...base,
      calculatedExplanation:
        "Calculated at checkout by a custom rate rule configured for this experience.",
    };
  }

  if (rateTable.type === RateTableType.DYNAMIC) {
    return {
      ...base,
      calculatedExplanation: dynamicRateExplanation(rateTable),
    };
  }

  const rate = rateTable.rates[0];
  const basePrice = rate ? Number(rate.price) : 0;
  const currency = parseCurrency(rate?.currency);
  const discount = rateTable.shippingDiscountRules?.[0]?.discount;
  const discountedPrice = applyDiscount(basePrice, discount);
  const savings = basePrice - discountedPrice;

  if (savings > 0) {
    return {
      ...base,
      priceLabel:
        discountedPrice <= 0 ? "FREE" : moneyAmount(discountedPrice, currency),
      originalPriceLabel: moneyAmount(basePrice, currency),
      savingsLabel: savingsLabel(discount, savings, currency),
    };
  }

  return {
    ...base,
    priceLabel: basePrice <= 0 ? "FREE" : moneyAmount(basePrice, currency),
  };
}

function dynamicRateExplanation(
  rateTable: Extract<ShippingRateTable, { type: RateTableType.DYNAMIC }>,
): string {
  const bounds = rateTable.priceBounds;
  if (bounds && (bounds.minimum != null || bounds.maximum != null)) {
    const currency = parseCurrency(bounds.currency);
    const min =
      bounds.minimum != null
        ? moneyAmount(Number(bounds.minimum), currency)
        : null;
    const max =
      bounds.maximum != null
        ? moneyAmount(Number(bounds.maximum), currency)
        : null;
    const range =
      min && max
        ? `between ${min} and ${max}`
        : min
          ? `at least ${min}`
          : max
            ? `up to ${max}`
            : null;
    if (range) {
      return `Calculated live at checkout from the carrier's real-time rate (${range}).`;
    }
  }
  return "Calculated live at checkout from the carrier's real-time rate.";
}

function applyDiscount(
  basePrice: number,
  discount: { percentage?: string; amount?: { value: string } } | undefined,
): number {
  if (!discount || basePrice <= 0) {
    return basePrice;
  }
  if (discount.percentage != null) {
    const pct = Number(discount.percentage);
    return Math.max(0, basePrice * (1 - pct / 100));
  }
  if (discount.amount != null) {
    return Math.max(0, basePrice - Number(discount.amount.value));
  }
  return basePrice;
}

function savingsLabel(
  discount: { percentage?: string } | undefined,
  savings: number,
  currency: Currency,
): string {
  if (discount?.percentage != null) {
    return `Enjoy ${Number(discount.percentage)}% off (save ${moneyAmount(savings, currency)})`;
  }
  return `Save ${moneyAmount(savings, currency)}`;
}

function moneyAmount(amount: number, currency: Currency): string {
  return moneyFormat({ amount: Math.round(amount * 100) / 100, currency });
}

function parseCurrency(currency: string | undefined): Currency {
  return safeParseEnum(currency, Currency) ?? DEFAULT_CURRENCY;
}
